import { createSlice } from '@reduxjs/toolkit';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import {
  createCompanyEmployee,
  fetchCompanyEmployees,
  fetchCompanyEmployee,
  updateCompanyEmployee,
  deleteCompanyEmployee,
  restoreCompanyEmployee,
  activateCompanyEmployee,
  inactivateCompanyEmployee,
  uploadCompanyEmployeesCsv,
} from './companyEmployee.actions';
import { CompanyEmployeeState } from './companyEmployee.types';

const initialState: CompanyEmployeeState = {
  companyEmployees: [],
  selectedCompanyEmployee: null,
  totalCompanyEmployees: 0,
  fetchCompanyEmployeesStatus: fetchStatuses.idle,
  fetchCompanyEmployeeStatus: fetchStatuses.idle,
  createCompanyEmployeeStatus: fetchStatuses.idle,
  updateCompanyEmployeeStatus: fetchStatuses.idle,
  deleteCompanyEmployeeStatus: fetchStatuses.idle,
  restoreCompanyEmployeeStatus: fetchStatuses.idle,
  activateCompanyEmployeeStatus: fetchStatuses.idle,
  inactivateCompanyEmployeeStatus: fetchStatuses.idle,
  uploadCompanyEmployeesCsvStatus: fetchStatuses.idle,
  error: null,
};

const companyEmployeeSlice = createSlice({
  name: 'companyEmployee',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // === FETCH COMPANY EMPLOYEES ===
    builder.addCase(fetchCompanyEmployees.pending, (state) => {
      state.error = null;
      state.fetchCompanyEmployeesStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanyEmployees.fulfilled, (state, action) => {
      state.fetchCompanyEmployeesStatus = fetchStatuses.success;
      state.companyEmployees = action.payload.data?.data ?? [];
      state.totalCompanyEmployees = action.payload.data?.total ?? 0;
    });
    builder.addCase(fetchCompanyEmployees.rejected, (state, action) => {
      state.fetchCompanyEmployeesStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY EMPLOYEE ===
    builder.addCase(fetchCompanyEmployee.pending, (state) => {
      state.error = null;
      state.fetchCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanyEmployee.fulfilled, (state, action) => {
      state.fetchCompanyEmployeeStatus = fetchStatuses.success;
      state.selectedCompanyEmployee = action.payload.data || null;
    });
    builder.addCase(fetchCompanyEmployee.rejected, (state, action) => {
      state.fetchCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === CREATE COMPANY EMPLOYEE ===
    builder.addCase(createCompanyEmployee.pending, (state) => {
      state.error = null;
      state.createCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(createCompanyEmployee.fulfilled, (state) => {
      state.createCompanyEmployeeStatus = fetchStatuses.success;
    });
    builder.addCase(createCompanyEmployee.rejected, (state, action) => {
      state.createCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPDATE COMPANY EMPLOYEE ===
    builder.addCase(updateCompanyEmployee.pending, (state) => {
      state.error = null;
      state.updateCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(updateCompanyEmployee.fulfilled, (state) => {
      state.updateCompanyEmployeeStatus = fetchStatuses.success;
    });
    builder.addCase(updateCompanyEmployee.rejected, (state, action) => {
      state.updateCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === DELETE COMPANY EMPLOYEE ===
    builder.addCase(deleteCompanyEmployee.pending, (state) => {
      state.error = null;
      state.deleteCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(deleteCompanyEmployee.fulfilled, (state) => {
      state.deleteCompanyEmployeeStatus = fetchStatuses.success;
    });
    builder.addCase(deleteCompanyEmployee.rejected, (state, action) => {
      state.deleteCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === RESTORE COMPANY EMPLOYEE ===
    builder.addCase(restoreCompanyEmployee.pending, (state) => {
      state.error = null;
      state.restoreCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(restoreCompanyEmployee.fulfilled, (state) => {
      state.restoreCompanyEmployeeStatus = fetchStatuses.success;
    });
    builder.addCase(restoreCompanyEmployee.rejected, (state, action) => {
      state.restoreCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === ACTIVATE COMPANY EMPLOYEE ===
    builder.addCase(activateCompanyEmployee.pending, (state) => {
      state.error = null;
      state.activateCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(activateCompanyEmployee.fulfilled, (state) => {
      state.activateCompanyEmployeeStatus = fetchStatuses.success;
    });
    builder.addCase(activateCompanyEmployee.rejected, (state, action) => {
      state.activateCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === INACTIVATE COMPANY EMPLOYEE ===
    builder.addCase(inactivateCompanyEmployee.pending, (state) => {
      state.error = null;
      state.inactivateCompanyEmployeeStatus = fetchStatuses.pending;
    });
    builder.addCase(inactivateCompanyEmployee.fulfilled, (state) => {
      state.inactivateCompanyEmployeeStatus = fetchStatuses.success;
    });
    builder.addCase(inactivateCompanyEmployee.rejected, (state, action) => {
      state.inactivateCompanyEmployeeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPLOAD COMPANY EMPLOYEES CSV ===
    builder.addCase(uploadCompanyEmployeesCsv.pending, (state) => {
      state.error = null;
      state.uploadCompanyEmployeesCsvStatus = fetchStatuses.pending;
    });
    builder.addCase(uploadCompanyEmployeesCsv.fulfilled, (state) => {
      state.uploadCompanyEmployeesCsvStatus = fetchStatuses.success;
    });
    builder.addCase(uploadCompanyEmployeesCsv.rejected, (state, action) => {
      state.uploadCompanyEmployeesCsvStatus = fetchStatuses.rejected;
      state.error = action.error;
    });
  },
});

export default companyEmployeeSlice.reducer;
